/**
 * extractionService.js
 *
 * Pure function service for AI-powered checklist extraction.
 * NO localStorage access - input → output only.
 * All state management handled by hooks.
 */

import {
  EXTRACTION_SYSTEM_PROMPT,
  createExtractionPrompt,
  CHECKLIST_TYPE_CONFIG,
  EXTRACTED_ITEM_SCHEMA
} from '../../config/operations/checklistImportConfig';

// ============================================================================
// HELPER FUNCTIONS (must be defined first)
// ============================================================================

/**
 * Calculate overall confidence from item confidences
 * @param {Array} items - Extracted items with confidence scores
 * @returns {number} Overall confidence (0.0 - 1.0)
 */
export const calculateOverallConfidence = (items) => {
  if (!items || items.length === 0) return 0;
  const sum = items.reduce((acc, item) => acc + (item.confidence || 0), 0);
  return Math.round((sum / items.length) * 100) / 100;
};

/**
 * Simple text similarity calculation (Jaccard similarity)
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {number} Similarity score (0.0 - 1.0)
 */
export const calculateTextSimilarity = (text1, text2) => {
  const words1 = new Set(text1.split(/\s+/).filter(w => w.length > 2));
  const words2 = new Set(text2.split(/\s+/).filter(w => w.length > 2));

  if (words1.size === 0 && words2.size === 0) return 1;
  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
};

/**
 * Group items by their detected section
 * @param {Array} items - Items with section property
 * @returns {Array} Items grouped by section
 */
export const groupItemsBySection = (items) => {
  const groups = {};

  items.forEach(item => {
    const section = item.section || 'General';
    if (!groups[section]) {
      groups[section] = {
        name: section,
        items: []
      };
    }
    groups[section].items.push(item);
  });

  return Object.values(groups);
};

/**
 * Detect roles mentioned in items (for familiarisation checklists)
 * @param {Array} items - Extracted items
 * @returns {Array} Detected roles
 */
export const detectRolesFromItems = (items) => {
  const roleKeywords = ['master', 'skipper', 'engineer', 'deckhand', 'mate', 'crew', 'all'];
  const detectedRoles = new Set();

  items.forEach(item => {
    const text = item.text.toLowerCase();
    roleKeywords.forEach(role => {
      if (text.includes(role)) {
        detectedRoles.add(role.charAt(0).toUpperCase() + role.slice(1));
      }
    });
  });

  return Array.from(detectedRoles);
};

/**
 * Parse and validate extraction response
 * @param {string} responseText - Raw text from Claude
 * @returns {Object} Parsed and validated extraction data
 */
export const parseExtractionResponse = (responseText) => {
  // Try to extract JSON from response (handle markdown code blocks)
  let jsonStr = responseText;

  // Remove markdown code blocks if present
  const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  // Try to find JSON object in text
  const jsonObjectMatch = jsonStr.match(/\{[\s\S]*\}/);
  if (jsonObjectMatch) {
    jsonStr = jsonObjectMatch[0];
  }

  try {
    const parsed = JSON.parse(jsonStr.trim());

    // Validate and normalize items
    const items = (parsed.items || []).map((item, index) => ({
      ...EXTRACTED_ITEM_SCHEMA,
      id: `extracted-${Date.now()}-${index}`,
      text: item.text || '',
      required: item.required !== false,
      regulatoryRef: item.regulatoryRef || null,
      confidence: typeof item.confidence === 'number' ? item.confidence : 0.5,
      section: item.section || null,
      notes: item.notes || null,
      originalIndex: index
    }));

    return {
      title: parsed.title || 'Imported Checklist',
      items,
      metadata: {
        totalItems: items.length,
        sectionsFound: parsed.metadata?.sectionsFound || [],
        regulatoryRefsFound: parsed.metadata?.regulatoryRefsFound || [],
        overallConfidence: parsed.metadata?.overallConfidence || calculateOverallConfidence(items)
      }
    };
  } catch (error) {
    console.error('Failed to parse extraction response:', error);
    throw new Error('Failed to parse AI response. Please try again.');
  }
};

// ============================================================================
// FILE PROCESSING
// ============================================================================

/**
 * Convert file to base64 for API submission
 * @param {File} file - The uploaded file
 * @returns {Promise<string>} Base64 encoded content
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Generate SHA-256 hash of file for audit trail
 * @param {File} file - The uploaded file
 * @returns {Promise<string>} SHA-256 hash
 */
export const generateFileHash = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return `sha256:${hashHex}`;
};

/**
 * Determine media type for Claude API
 * @param {File} file - The uploaded file
 * @returns {string} Media type string
 */
export const getMediaType = (file) => {
  const mimeMap = {
    'application/pdf': 'application/pdf',
    'image/jpeg': 'image/jpeg',
    'image/png': 'image/png',
    'image/jpg': 'image/jpeg'
  };
  return mimeMap[file.type] || 'application/octet-stream';
};

// ============================================================================
// AI EXTRACTION
// ============================================================================

/**
 * Extract checklist items from document using Claude API
 * This is a PURE function - no side effects, no localStorage
 *
 * @param {Object} params - Extraction parameters
 * @param {string} params.base64Content - Base64 encoded file content
 * @param {string} params.mediaType - MIME type of the file
 * @param {string} params.checklistType - Type of checklist being imported
 * @param {string} params.apiKey - Claude API key
 * @param {string} params.model - Model to use (haiku/opus)
 * @returns {Promise<Object>} Extraction result
 */
export const extractChecklistItems = async ({
  base64Content,
  mediaType,
  checklistType,
  apiKey,
  model = 'claude-3-haiku-20240307'
}) => {
  const typeConfig = CHECKLIST_TYPE_CONFIG[checklistType];
  if (!typeConfig) {
    throw new Error(`Unknown checklist type: ${checklistType}`);
  }

  const userPrompt = createExtractionPrompt(checklistType, typeConfig.schema);

  // Build message content based on media type
  const content = mediaType.startsWith('image/') || mediaType === 'application/pdf'
    ? [
        {
          type: 'image',
          source: {
            type: 'base64',
            media_type: mediaType,
            data: base64Content
          }
        },
        {
          type: 'text',
          text: userPrompt
        }
      ]
    : [
        {
          type: 'text',
          text: `Document content:\n\n${atob(base64Content)}\n\n${userPrompt}`
        }
      ];

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: EXTRACTION_SYSTEM_PROMPT,
      messages: [
        {
          role: 'user',
          content
        }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `API error: ${response.status}`);
  }

  const result = await response.json();
  const textContent = result.content?.find(c => c.type === 'text')?.text;

  if (!textContent) {
    throw new Error('No text content in API response');
  }

  // Parse JSON from response
  const parsed = parseExtractionResponse(textContent);

  return {
    success: true,
    data: parsed,
    model,
    usage: result.usage
  };
};

// ============================================================================
// ITEM MANIPULATION (Pure functions)
// ============================================================================

/**
 * Add a new item to the list
 * @param {Array} items - Current items
 * @param {Object} newItem - Item to add
 * @returns {Array} Updated items array
 */
export const addItem = (items, newItem) => {
  const item = {
    ...EXTRACTED_ITEM_SCHEMA,
    id: `manual-${Date.now()}`,
    text: newItem.text || '',
    required: newItem.required !== false,
    confidence: 1.0, // Manual items have full confidence
    notes: 'Manually added',
    originalIndex: items.length
  };
  return [...items, item];
};

/**
 * Update an item in the list
 * @param {Array} items - Current items
 * @param {string} itemId - ID of item to update
 * @param {Object} updates - Fields to update
 * @returns {Array} Updated items array
 */
export const updateItem = (items, itemId, updates) => {
  return items.map(item =>
    item.id === itemId
      ? { ...item, ...updates }
      : item
  );
};

/**
 * Remove an item from the list
 * @param {Array} items - Current items
 * @param {string} itemId - ID of item to remove
 * @returns {Array} Updated items array
 */
export const removeItem = (items, itemId) => {
  return items.filter(item => item.id !== itemId);
};

/**
 * Reorder items in the list
 * @param {Array} items - Current items
 * @param {number} fromIndex - Source index
 * @param {number} toIndex - Destination index
 * @returns {Array} Updated items array
 */
export const reorderItems = (items, fromIndex, toIndex) => {
  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
};

// ============================================================================
// CONFLICT DETECTION
// ============================================================================

/**
 * Check for conflicts with existing regulatory items
 * @param {Array} importedItems - Items being imported
 * @param {Array} existingItems - Existing items in the system
 * @returns {Array} List of potential conflicts
 */
export const detectConflicts = (importedItems, existingItems) => {
  const conflicts = [];

  importedItems.forEach(imported => {
    const normalizedImportText = imported.text.toLowerCase().trim();

    existingItems.forEach(existing => {
      const normalizedExistingText = existing.text.toLowerCase().trim();

      // Check for similar text (simple similarity check)
      const similarity = calculateTextSimilarity(normalizedImportText, normalizedExistingText);

      if (similarity > 0.7) {
        conflicts.push({
          importedItem: imported,
          existingItem: existing,
          similarity,
          isLocked: existing.locked || existing.isRegulatory || false
        });
      }
    });
  });

  return conflicts;
};

// ============================================================================
// SCHEMA MAPPERS
// ============================================================================

/**
 * Map extracted items to the target checklist schema
 * @param {Object} extractionResult - Result from AI extraction
 * @param {string} checklistType - Target checklist type
 * @param {Object} metadata - Import metadata
 * @returns {Object} Formatted checklist ready for storage
 */
export const mapToChecklistSchema = (extractionResult, checklistType, metadata) => {
  // typeConfig can be used for type-specific validation in the future
  // const typeConfig = CHECKLIST_TYPE_CONFIG[checklistType];

  const baseChecklist = {
    id: `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    title: extractionResult.title,
    checklistType,
    isCustom: true,
    items: extractionResult.items.map(item => ({
      id: item.id,
      text: item.text,
      required: item.required,
      regulatoryRef: item.regulatoryRef,
      notes: item.notes
    })),
    ...metadata
  };

  // Add type-specific fields
  switch (checklistType) {
    case 'emergency':
      return {
        ...baseChecklist,
        steps: groupItemsBySection(extractionResult.items),
        conditions: []
      };

    case 'pre_departure':
      return {
        ...baseChecklist,
        tier: 'custom', // Not locked regulatory items
        items: extractionResult.items.map(item => ({
          ...item,
          locked: false,
          canToggle: true
        }))
      };

    case 'familiarisation':
      return {
        ...baseChecklist,
        roles: detectRolesFromItems(extractionResult.items),
        sections: extractionResult.metadata.sectionsFound
      };

    case 'navigation':
    case 'operational':
      return {
        ...baseChecklist,
        fields: [], // To be populated by user if needed
        groups: groupItemsBySection(extractionResult.items)
      };

    default:
      return baseChecklist;
  }
};
